"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface CacheEntry<T> {
  data: T;
  timestamp: string;
  page: number;
  filters?: Record<string, any>;
}

interface DataCacheConfig {
  cacheExpirySeconds?: number;
  includeFilters?: boolean;
}

interface CachedResult<T> {
  data: T;
  filters?: Record<string, any>;
}

export const SESSION_STORAGE_KEY_PREFIX = "gdc_";

/**
 * A reusable hook for caching paginated data with timestamp-based expiration.
 * Useful for preventing unnecessary API calls when users navigate back to a page
 * within a specified time window.
 *
 * @param cacheKey - A unique key to identify this cache instance
 * @param config - Configuration options for the cache
 * @returns Cache utilities for storing and retrieving data
 */
export function useDataCache<T>(
  cacheKey: string,
  config: DataCacheConfig = {}
) {
  const { cacheExpirySeconds = 60, includeFilters = false } = config;

  const storageKey = `${SESSION_STORAGE_KEY_PREFIX}${cacheKey}`;
  const cacheRef = useRef<Map<string, CacheEntry<T>>>(new Map());
  const [cachedData, setCachedData] = useState<T | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);

  // Save cache to sessionStorage
  const saveCacheToStorage = useCallback(() => {
    try {
      const cacheObject = Object.fromEntries(cacheRef.current);
      sessionStorage.setItem(storageKey, JSON.stringify(cacheObject));
    } catch (error) {
      console.warn("Failed to save cache to sessionStorage:", error);
    }
  }, [storageKey]);

  // Initialize cache from sessionStorage on mount
  const initializeCache = useCallback(() => {
    try {
      const storedCache = sessionStorage.getItem(storageKey);
      if (storedCache) {
        const parsedCache = JSON.parse(storedCache);
        const cacheMap = new Map<string, CacheEntry<T>>();

        // Convert stored object back to Map and validate entries
        Object.entries(parsedCache).forEach(([key, entry]) => {
          const cacheEntry = entry as CacheEntry<T>;
          // Inline cache validation to avoid circular dependency
          const cacheTime = new Date(cacheEntry.timestamp);
          const now = new Date();
          const diffInSeconds = (now.getTime() - cacheTime.getTime()) / 1000;

          if (diffInSeconds <= cacheExpirySeconds) {
            cacheMap.set(key, cacheEntry);
          }
        });

        cacheRef.current = cacheMap;
        saveCacheToStorage();
      }
    } catch (error) {
      console.warn("Failed to load cache from sessionStorage:", error);
      sessionStorage.removeItem(storageKey);
    }
  }, [storageKey, cacheExpirySeconds, saveCacheToStorage]);

  // Initialize cache on mount
  useEffect(() => {
    initializeCache();
  }, [initializeCache]);

  /**
   * Check if cached data exists and is still valid
   */
  const isCacheValid = useCallback(
    (timestamp: string): boolean => {
      const cacheTime = new Date(timestamp);
      const now = new Date();
      const diffInSeconds = (now.getTime() - cacheTime.getTime()) / 1000;

      return diffInSeconds <= cacheExpirySeconds;
    },
    [cacheExpirySeconds]
  );

  /**
   * Retrieve cached data for a specific page and filters if it exists and is valid
   */
  const getCachedData = useCallback(
    (page?: number, filters?: Record<string, any>): CachedResult<T> | null => {
      const entry = cacheRef.current.get(cacheKey);

      if (!entry || !isCacheValid(entry.timestamp)) {
        if (entry) {
          // Remove expired cache entry
          cacheRef.current.delete(cacheKey);
          saveCacheToStorage();
        }
        setIsFromCache(false);
        return null;
      }

      // If specific page/filters requested, check if they match
      if (page !== undefined && entry.page !== page) {
        setIsFromCache(false);
        return null;
      }

      if (includeFilters && filters) {
        const entryFilters = entry.filters || {};
        const filtersMatch = Object.entries(filters).every(
          ([key, value]) => entryFilters[key] === value
        );
        if (!filtersMatch) {
          setIsFromCache(false);
          return null;
        }
      }

      setCachedData(entry.data);
      setIsFromCache(true);
      return {
        data: entry.data,
        filters: entry.filters,
      };
    },
    [cacheKey, isCacheValid, includeFilters, saveCacheToStorage]
  );

  /**
   * Store data in cache with current timestamp
   */
  const setCacheData = useCallback(
    (data: T, page: number, filters?: Record<string, any>): void => {
      const entry: CacheEntry<T> = {
        data,
        timestamp: new Date().toISOString(),
        page,
        filters: includeFilters ? filters : undefined,
      };

      cacheRef.current.set(cacheKey, entry);
      saveCacheToStorage();
      setCachedData(data);
      setIsFromCache(false);
    },
    [cacheKey, includeFilters, saveCacheToStorage]
  );

  /**
   * Clear all cached data for this cache instance
   */
  const clearCache = useCallback((): void => {
    cacheRef.current.clear();
    sessionStorage.removeItem(storageKey);
    setCachedData(null);
    setIsFromCache(false);
  }, [storageKey]);

  /**
   * Clear specific cache entry
   */
  const clearCacheEntry = useCallback((): void => {
    cacheRef.current.delete(cacheKey);
    saveCacheToStorage();

    // If we just cleared the currently displayed data, reset state
    if (cachedData) {
      setCachedData(null);
      setIsFromCache(false);
    }
  }, [cacheKey, cachedData, saveCacheToStorage]);

  /**
   * Get cache statistics for debugging
   */
  const getCacheStats = useCallback(() => {
    const entry = cacheRef.current.get(cacheKey);
    const isValid = entry ? isCacheValid(entry.timestamp) : false;

    return {
      hasEntry: !!entry,
      isValid,
      entry: entry || null,
      cacheKey,
    };
  }, [cacheKey, isCacheValid]);

  return {
    getCachedData,
    setCacheData,
    clearCache,
    clearCacheEntry,
    getCacheStats,
    cachedData,
    isFromCache,
  };
}

export default useDataCache;
